import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderSlot as _renderSlot, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "modal-header" }
const _hoisted_2 = { class: "head__action" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "body" }
const _hoisted_5 = { class: "body__header" }
const _hoisted_6 = { class: "headline" }
const _hoisted_7 = {
  key: 0,
  class: "body__header--status"
}
const _hoisted_8 = { class: "body__header--title" }
const _hoisted_9 = { class: "body__header--time" }
const _hoisted_10 = { class: "body__main" }
const _hoisted_11 = {
  key: 0,
  class: "modal-footer"
}

import {
  X,
  Check,
  Shield02,
  File06,
  AlertCircle,
  MessageTextSquare02,
  Building07,
} from "utruckin-icons";
import { computed, onBeforeUnmount, onUnmounted, ref, watch } from "vue";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import { useNotificationStore } from "@/features/notification/notification.store";
import { INotificationItem } from "@/features/notification/notification.types";
import { DateTime } from "luxon";

type CloseReason = "overlay" | "esc" | "button" | "program";


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomNotificationModal',
  props: {
    modelValue: { type: Boolean },
    title: {},
    closeOnOverlay: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true },
    lockScroll: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "open", "close"],
  setup(__props: any, { emit: __emit }) {

const notificationStore = useNotificationStore();

const props = __props;

const emit = __emit;

const notificationLoading = ref<boolean>(false);
const notificationItems = ref<INotificationItem[]>([]);
const modalBody = ref<HTMLElement | null>(null);
const loadMoreTrigger = ref<HTMLElement | null>(null);
const hasMore = ref<boolean>(true);

const isOpen = computed(() => props.modelValue);

let mouseDownOnOverlay = false;
let observer: IntersectionObserver | null = null;

function setBodyLock(locked: boolean) {
  if (!props.lockScroll) return;
  document.body.style.overflow = locked ? "hidden" : "";
}

function close(reason: CloseReason = "program") {
  emit("close", reason);
  emit("update:modelValue", false);
}

function onOverlayMouseDown(e: MouseEvent) {
  mouseDownOnOverlay = e.target === e.currentTarget;
}

function onOverlayMouseUp() {
  if (props.closeOnOverlay && mouseDownOnOverlay) {
    close("overlay");
  }
  mouseDownOnOverlay = false;
}

function onKeydown(e: KeyboardEvent) {
  if (!props.closeOnEsc) return;
  if (e.key === "Escape" && isOpen.value) close("esc");
}

const closeModal = () => {
  close("overlay");
};

const clickNotification = async (item: INotificationItem) => {
  if (item.read_time) return;
  await notificationStore.notificationRead(
    notificationLoading,
    item.id,
    async (data: any) => {
      if (data) {
        await reload();

        await notificationStore.getCount(notificationLoading, (data: any) => {
          notificationStore.notificationCount = data.count;
        });
      }
    }
  );
};

const getList = async () => {
  if (notificationLoading.value || !hasMore.value) return;
  await notificationStore.getList(notificationLoading, (data: any) => {
    notificationItems.value.push(...data.items);
    const meta = data.meta;
    hasMore.value = meta?.page * meta.per_page < meta.total;

    notificationStore.notificationFilter.page =
      (notificationStore.notificationFilter.page as number) + 1;
  });
};

const reload = async () => {
  notificationItems.value = [];
  hasMore.value = true;
  notificationStore.notificationFilter.page = 1;

  await getList();
};

const utcIsoToZoneRelative = (value: string) =>
  DateTime.fromISO(value, { zone: "utc" })
    .setZone("America/New_York")
    .toRelative()!;

const readAll = async () => {
  await notificationStore.readAll(notificationLoading, async (data: any) => {
    if (data) {
      await reload();

      await notificationStore.getCount(notificationLoading, (data: any) => {
        notificationStore.notificationCount = data.count;
      });
    }
  });
};

watch(
  isOpen,
  async (v) => {
    if (v) {
      emit("open");
      setBodyLock(true);
      window.addEventListener("keydown", onKeydown);
      notificationItems.value = [];
      hasMore.value = true;
      notificationStore.notificationFilter.page = 1;

      await getList();

      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            getList();
          }
        },
        {
          root: modalBody.value,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      if (loadMoreTrigger.value) {
        observer.observe(loadMoreTrigger.value);
      }
    } else {
      setBodyLock(false);
      window.removeEventListener("keydown", onKeydown);
      observer?.disconnect();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  setBodyLock(false);
  window.removeEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  observer?.disconnect();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "modal-fade" }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "modal-overlay",
              onMousedown: onOverlayMouseDown,
              onMouseup: onOverlayMouseUp
            }, [
              _createElementVNode("div", {
                class: "modal-card",
                role: "dialog",
                "aria-modal": "true",
                onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                onMouseup: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createElementVNode("header", _hoisted_1, [
                  _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "modal-title" }, "All notifications", -1)),
                  _createElementVNode("div", {
                    class: "modal-close",
                    onClick: closeModal
                  }, [
                    _createVNode(_unref(X), { size: 24 })
                  ])
                ]),
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(CommonButton, {
                    type: "secondary",
                    label: "Read all",
                    prepend: "",
                    prependIcon: _unref(Check),
                    onClicked: readAll
                  }, null, 8, ["prependIcon"])
                ]),
                _createElementVNode("div", {
                  class: "modal-body",
                  ref_key: "modalBody",
                  ref: modalBody
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notificationItems.value, (item) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: "notification__item",
                      key: item.id,
                      onClick: ($event: any) => (clickNotification(item))
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["icon", item.type])
                      }, [
                        (item.type == 'task_assigned')
                          ? (_openBlock(), _createBlock(_unref(File06), {
                              key: 0,
                              size: 20
                            }))
                          : (item.type == 'qa_failed')
                            ? (_openBlock(), _createBlock(_unref(AlertCircle), {
                                key: 1,
                                size: 20
                              }))
                            : (item.type == 'task_comment_added')
                              ? (_openBlock(), _createBlock(_unref(MessageTextSquare02), {
                                  key: 2,
                                  size: 20
                                }))
                              : (item.type == 'company_created_without_team')
                                ? (_openBlock(), _createBlock(_unref(Building07), {
                                    key: 3,
                                    size: 20
                                  }))
                                : (_openBlock(), _createBlock(_unref(Shield02), {
                                    key: 4,
                                    size: 20
                                  }))
                      ], 2),
                      _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("div", _hoisted_6, [
                            (!item.read_time)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_8, _toDisplayString(item.title), 1)
                          ]),
                          _createElementVNode("div", _hoisted_9, _toDisplayString(utcIsoToZoneRelative(item.created_time)), 1)
                        ]),
                        _createElementVNode("div", _hoisted_10, _toDisplayString(item.body), 1)
                      ])
                    ], 8, _hoisted_3))
                  }), 128)),
                  _createElementVNode("div", {
                    ref_key: "loadMoreTrigger",
                    ref: loadMoreTrigger
                  }, null, 512)
                ], 512),
                (_ctx.$slots.footer)
                  ? (_openBlock(), _createElementBlock("footer", _hoisted_11, [
                      _renderSlot(_ctx.$slots, "footer")
                    ]))
                  : _createCommentVNode("", true)
              ], 32)
            ], 32))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ]))
}
}

})